import { getTariffCalculationEligibility } from '@billing/validation/tariffCalculationRules';
import type { ProviderClaimStatusValue } from '@billing/validation/providerClaimStatus';
import {
  CLAIM_TARIFF_STATUS,
  CLAIM_TARIFF_STATUS_BAND,
  claimTariffStatusEnumValues,
  type ClaimTariffStatusBandValue,
} from '@billing/validation/tariff';
import { providerClaimStatusDefinitions } from 'utils/providerClaimStatus';
import {
  isClerkClaimTab,
  type ClaimTabKey,
  type ClerkClaimTabKey,
} from 'store/ClaimTabFilterStore';

type TariffStatus = (typeof claimTariffStatusEnumValues)[number];

const TAB_STATUSES: Record<ClerkClaimTabKey, TariffStatus[]> = {
  all: [],
  actionNeeded: [],
  preSubmission: [CLAIM_TARIFF_STATUS.INCOMPLETE, CLAIM_TARIFF_STATUS.READY],
  underBillerReview: [
    CLAIM_TARIFF_STATUS.REJECTED,
    CLAIM_TARIFF_STATUS.UNMATCHED,
    CLAIM_TARIFF_STATUS.OVERPAID,
    CLAIM_TARIFF_STATUS.UNDERPAID,
    CLAIM_TARIFF_STATUS.PENDING_WRITE_OFF,
    CLAIM_TARIFF_STATUS.REVERTED,
    CLAIM_TARIFF_STATUS.UNINSURED,
  ],
  submittedToPayor: [
    CLAIM_TARIFF_STATUS.SUBMITTED,
    CLAIM_TARIFF_STATUS.RESUBMITTED,
  ],
  underPayorReview: [CLAIM_TARIFF_STATUS.PENDING, CLAIM_TARIFF_STATUS.QUERIED],
  invoiced: [CLAIM_TARIFF_STATUS.PAYOR_INVOICED],
  completed: [
    CLAIM_TARIFF_STATUS.PAID,
    CLAIM_TARIFF_STATUS.WRITTEN_OFF,
    CLAIM_TARIFF_STATUS.UNCLAIMABLE,
  ],
};

export const getTariffStatusesForTab = (tab: ClerkClaimTabKey): string[] =>
  Array.from(new Set(TAB_STATUSES[tab] ?? []));

export const getStatusSelectOptions = (
  activeTab: ClaimTabKey,
  isProvider: boolean,
  translate: (key: string, fallback: string) => string
): { value: string; label: string; disabled: boolean }[] => {
  if (isProvider) {
    const bands: Partial<Record<ClaimTabKey, ClaimTariffStatusBandValue>> = {
      preSubmission: CLAIM_TARIFF_STATUS_BAND.PRE_SUBMISSION,
      outstanding: CLAIM_TARIFF_STATUS_BAND.OUTSTANDING,
      completed: CLAIM_TARIFF_STATUS_BAND.COMPLETED,
    };
    return PROVIDER_STATUS_SELECT_DEFINITIONS.map(
      ({ key, defaultLabel, band }) => ({
        value: key,
        label: translate(
          `pages:claims.providerSubStatusFilters.${key}`,
          defaultLabel
        ),
        disabled:
          activeTab !== 'all' &&
          band !== bands[activeTab] &&
          !(key === 'readyToSubmit' && activeTab === 'outstanding'),
      })
    );
  }
  const allowed = isClerkClaimTab(activeTab)
    ? getTariffStatusesForTab(activeTab)
    : [];
  return claimTariffStatusEnumValues.map((status) => ({
    value: status,
    label: translate(`pages:claims.statusFilterOptions.${status}`, status),
    disabled:
      activeTab !== 'all' &&
      activeTab !== 'actionNeeded' &&
      !allowed.includes(status),
  }));
};

interface ProviderSubStatusDefinition {
  key: string;
  defaultLabel: string;
  statuses: ProviderClaimStatusValue[];
  band: ClaimTariffStatusBandValue;
}

const createProviderStatusFilter = (
  key: string,
  status: ProviderClaimStatusValue
): ProviderSubStatusDefinition => {
  const { label, band } = providerClaimStatusDefinitions[status];
  const equivalentStatuses = claimTariffStatusEnumValues
    .filter(
      (candidate): candidate is ProviderClaimStatusValue =>
        candidate in providerClaimStatusDefinitions
    )
    .filter((candidate) => {
      const definition = providerClaimStatusDefinitions[candidate];
      return (
        candidate !== status &&
        definition.label === label &&
        definition.band === band
      );
    });
  return {
    key,
    defaultLabel: label,
    statuses: [status, ...equivalentStatuses],
    band,
  };
};

export const PROVIDER_STATUS_SELECT_DEFINITIONS: readonly ProviderSubStatusDefinition[] =
  [
    createProviderStatusFilter('draft', CLAIM_TARIFF_STATUS.INCOMPLETE),
    createProviderStatusFilter('readyToSubmit', CLAIM_TARIFF_STATUS.READY),
    createProviderStatusFilter('submitted', CLAIM_TARIFF_STATUS.SUBMITTED),
    createProviderStatusFilter('rejected', CLAIM_TARIFF_STATUS.REJECTED),
    createProviderStatusFilter('paidShort', CLAIM_TARIFF_STATUS.UNDERPAID),
    createProviderStatusFilter(
      'pendingWriteOff',
      CLAIM_TARIFF_STATUS.PENDING_WRITE_OFF
    ),
    createProviderStatusFilter('resubmitted', CLAIM_TARIFF_STATUS.RESUBMITTED),
    createProviderStatusFilter('pending', CLAIM_TARIFF_STATUS.PENDING),
    createProviderStatusFilter('queried', CLAIM_TARIFF_STATUS.QUERIED),
    createProviderStatusFilter('uninsured', CLAIM_TARIFF_STATUS.UNINSURED),
    createProviderStatusFilter(
      'payorInvoiced',
      CLAIM_TARIFF_STATUS.PAYOR_INVOICED
    ),
    createProviderStatusFilter('paid', CLAIM_TARIFF_STATUS.PAID),
    createProviderStatusFilter('writtenOff', CLAIM_TARIFF_STATUS.WRITTEN_OFF),
    createProviderStatusFilter('unclaimable', CLAIM_TARIFF_STATUS.UNCLAIMABLE),
  ];

const BILLER_REVIEW_FILTER_KEYS = ['rejected', 'paidShort', 'pendingWriteOff'];

export const PROVIDER_SUB_STATUS_DEFINITIONS: readonly ProviderSubStatusDefinition[] =
  PROVIDER_STATUS_SELECT_DEFINITIONS.flatMap((definition) => {
    if (definition.key === 'rejected') {
      return [
        {
          key: 'withYourBiller',
          defaultLabel: 'With QBilling',
          statuses: PROVIDER_STATUS_SELECT_DEFINITIONS.filter(({ key }) =>
            BILLER_REVIEW_FILTER_KEYS.includes(key)
          ).flatMap(({ statuses }) => statuses),
          band: definition.band,
        },
      ];
    }
    return BILLER_REVIEW_FILTER_KEYS.includes(definition.key)
      ? []
      : [definition];
  });

export const getProviderStatusesForBand = (
  band: ClaimTariffStatusBandValue
): string[] => {
  if (band === CLAIM_TARIFF_STATUS_BAND.OUTSTANDING) {
    return claimTariffStatusEnumValues.filter(
      (status) => getTariffCalculationEligibility(status, true).outstanding
    );
  }
  return Object.entries(providerClaimStatusDefinitions)
    .filter(([, definition]) => definition.band === band)
    .map(([status]) => status);
};
